import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSession } from "@clerk/react";
import { Button, toast, useOverlayState } from "@heroui/react";
import { useMediaQuery } from "react-responsive";
import { RefreshCw } from "lucide-react";
import RecordingsTable from "@/components/tables/recordings-table";
import RecordingDetailsComp from "@/components/desktop/recordings/recording-details-comp";
import RecordingDetailsDrawer from "@/components/desktop/drawers/recording-details-drawer";
import ErrorBoundary from "@/components/error-boundries/error-boundry-comp";
import { getUserRecordings as getUserRecordingsApi } from "@/api/audio";
import { getPastDateRange } from "@/utils/dates";
import { cn } from "@/utils/cn";

const sortByDateDesc = (records) =>
  [...records].sort((a, b) => {
    const aKey = `${a.meeting_date} ${a.start_time?.set ?? ""}`;
    const bKey = `${b.meeting_date} ${b.start_time?.set ?? ""}`;
    return bKey.localeCompare(aKey);
  });

const RecordingsPage = () => {
  const { session } = useSession();
  const [recordings, setRecordings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [refreshKey, setRefreshKey] = useState(0);

  const isTablet = useMediaQuery({ maxWidth: 1100 });
  const detailsState = useOverlayState();

  const sortedRecords = useMemo(() => sortByDateDesc(recordings), [recordings]);

  useEffect(() => {
    getUserRecordings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const selectedRecordingId = searchParams.get("selected") || sortedRecords[0]?._id;
  useEffect(() => {
    if (isTablet && searchParams.get("selected")) detailsState.open();
  }, [isTablet, searchParams]);

  const handleSelectRecording = (id) => {
    setSearchParams((prev) => {
      prev.set("selected", id);
      return prev;
    });

    if (isTablet) detailsState.open();
  };

  const getUserRecordings = async () => {
    if (!session) return;

    setIsLoading(true);
    const token = await session.getToken();

    return getUserRecordingsApi("current", ["1970-01-01", "9999-12-31"], token)
      .then((_resp) => {
        if (_resp.data.Result === "OK") {
          setRecordings(_resp.data.Records ?? []);
        } else {
          throw new Error("Couldn't fetch recordings");
        }
      })
      .catch((err) => {
        console.log(err);
        toast.warning("Could not fetch recordings");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleRefresh = async () => {
    setRefreshKey((prev) => prev + 1);
    await getUserRecordings();
  };

  return (
    <div className="flex flex-col gap-2 desktop-page-padding">
      <div className="flex items-center justify-between">
        <div>
          <span className="text-2xl flex items-center font-extrabold">Recordings</span>

          <span className="text-sm text-muted">All your loop recordings, transcripts and insights</span>
        </div>

        <div className="flex items-center gap-2">
          {/* <Button
            size="md"
            variant="outline"
            className={cn("border rounded-full bg-surface", showFilters && "bg-accent/8 text-purple-foreground")}
            onPress={() => setShowFilters((prev) => !prev)}
          >
            <Filter className={cn(showFilters && "text-purple-foreground")} />
            Filter
            {activeFilterCount > 0 && (
              <Chip variant="soft" color="accent" size="sm">
                <Chip.Label>{activeFilterCount}</Chip.Label>
              </Chip>
            )}
            <div className={cn("transition-transform duration-200", showFilters ? "rotate-180" : "rotate-0")}>
              <ChevronDown className={cn(showFilters && "text-purple-foreground")} />
            </div>
          </Button> */}

          <Button
            variant="outline"
            isIconOnly
            className="bg-surface text-foreground rounded-xl font-normal"
            isPending={isLoading}
            onPress={handleRefresh}
            aria-label="Refresh recordings"
          >
            <RefreshCw size={14} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="lg:col-span-7 col-span-12">
          <RecordingsTable
            recordings={sortedRecords}
            isLoading={isLoading}
            selectedRecordingId={selectedRecordingId}
            onSelectRecording={handleSelectRecording}
          />
        </div>

        {!isTablet && (
          <div className="lg:col-span-5 sticky top-6 self-start max-h-full">
            <ErrorBoundary>
              <RecordingDetailsComp _recording_id={selectedRecordingId} refreshKey={refreshKey} onDeleted={() => { detailsState.close(); setSearchParams({}); handleRefresh(); }} />
            </ErrorBoundary>
          </div>
        )}
      </div>

      {isTablet && (
        <RecordingDetailsDrawer _recording_id={selectedRecordingId} state={detailsState} refreshKey={refreshKey} onDeleted={() => { detailsState.close(); setSearchParams({}); handleRefresh(); }} />
      )}
    </div>
  );
};

export default RecordingsPage;